import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { AuditFormModal } from '../features/audits/AuditFormModal';
import {
  createAudit,
  createAuditProgram,
  fetchAuditPrograms,
  fetchAudits,
} from '../features/audits/auditsApi';
import type { AuditProgram, AuditStatus, AuditSummary, AuditType } from '../features/audits/types';
import {
  AUDIT_STATUS_LABEL,
  AUDIT_TYPE_LABEL,
  auditStatusBadgeClass,
} from '../features/audits/auditUtils';
import { DateCell } from '../components/DateCell';

const STATUSES: AuditStatus[] = ['planificada', 'en_curso', 'informe_emitido', 'cerrada'];
const TYPES: AuditType[] = ['interna', 'externa'];

export function AuditsPage() {
  const { canEdit } = usePlatformAccessLevel('audits.access');
  const [programs, setPrograms] = useState<AuditProgram[]>([]);
  const [audits, setAudits] = useState<AuditSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [programYear, setProgramYear] = useState(String(new Date().getFullYear()));
  const [programObjectives, setProgramObjectives] = useState('');
  const [creatingProgram, setCreatingProgram] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [programList, auditList] = await Promise.all([
        fetchAuditPrograms(),
        fetchAudits({
          page,
          pageSize: 20,
          status: filterStatus || undefined,
          type: filterType || undefined,
          year: filterYear ? Number.parseInt(filterYear, 10) : undefined,
        }),
      ]);
      setPrograms(programList);
      setAudits(auditList.data);
      setTotalPages(auditList.pagination.pages);
    } catch {
      setError('No se pudieron cargar las auditorías.');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterType, filterYear]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreateProgram = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingProgram(true);
    try {
      await createAuditProgram({
        year: Number.parseInt(programYear, 10),
        objectives: programObjectives.trim() || null,
      });
      setProgramObjectives('');
      await load();
    } finally {
      setCreatingProgram(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Auditorías</h1>
          <p className="text-muted-foreground text-sm">
            Programa anual, ejecución y hallazgos (ISO 9.2)
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Nueva auditoría
          </button>
        )}
      </div>

      {canEdit && (
        <section className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h2 className="font-semibold text-sm">Programa anual</h2>
          {programs.length > 0 ? (
            <ul className="text-sm space-y-1">
              {programs.map((p) => (
                <li key={p.id}>
                  <span className="font-medium">{p.year}</span>
                  {p._count && (
                    <span className="text-muted-foreground"> — {p._count.audits} auditoría(s)</span>
                  )}
                  {p.objectives && (
                    <p className="text-muted-foreground text-xs mt-0.5">{p.objectives}</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sin programas registrados.</p>
          )}
          <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => void handleCreateProgram(e)}>
            <label className="text-sm">
              Año
              <input
                type="number"
                className="block mt-1 rounded-md border border-border px-3 py-2 text-sm w-28"
                value={programYear}
                onChange={(e) => setProgramYear(e.target.value)}
                min={2000}
                max={2100}
                placeholder="2026"
              />
            </label>
            <label className="text-sm flex-1 min-w-[200px]">
              Objetivos del programa
              <input
                className="block mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={programObjectives}
                onChange={(e) => setProgramObjectives(e.target.value)}
                placeholder="Opcional"
              />
            </label>
            <button type="submit" className="btn btn-outline" disabled={creatingProgram}>
              {creatingProgram ? 'Creando…' : 'Crear programa'}
            </button>
          </form>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {AUDIT_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los tipos</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {AUDIT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <input
          type="number"
          className="rounded-md border border-border px-3 py-2 text-sm w-28"
          placeholder="Año"
          value={filterYear}
          onChange={(e) => {
            setFilterYear(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted/50 text-muted-foreground border-b border-border">
            <tr>
              <th className="px-4 py-3 font-medium">Alcance</th>
              <th className="px-4 py-3 font-medium">Tipo</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Planificada</th>
              <th className="px-4 py-3 font-medium">Programa</th>
              <th className="px-4 py-3 font-medium">Hallazgos</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : audits.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Sin auditorías.
                </td>
              </tr>
            ) : (
              audits.map((audit) => (
                <tr key={audit.id} className="border-b border-border hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link to={`/auditorias/${audit.id}`} className="text-primary hover:underline">
                      {audit.scope.slice(0, 80)}
                      {audit.scope.length > 80 ? '…' : ''}
                    </Link>
                    {audit.auditeeArea && (
                      <p className="text-xs text-muted-foreground">{audit.auditeeArea}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">{AUDIT_TYPE_LABEL[audit.type]}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${auditStatusBadgeClass(audit.status)}`}
                    >
                      {AUDIT_STATUS_LABEL[audit.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <DateCell value={audit.plannedDate} />
                  </td>
                  <td className="px-4 py-3">{audit.program?.year ?? '—'}</td>
                  <td className="px-4 py-3">{audit._count?.findings ?? 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground self-center">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <AuditFormModal
        open={modalOpen}
        programs={programs}
        onClose={() => setModalOpen(false)}
        onSubmit={async (payload) => {
          await createAudit(payload);
          await load();
        }}
      />
    </div>
  );
}
